import './InquiryPages.css';
import { Check, Clock, Mail, MapPin, MessageSquare, Phone, Timer, UserRound } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import GoogleMaps from '../components/GoogleMaps';
import SEO from '../components/SEO';
import InquiryJourney, { Field, Privacy } from '../components/InquiryJourney';
import { CLUB_ADDRESS } from '../components/ProgramJourney';

// Contact as a journey like Book a Tour (owner direction, Sept 30). It sends exactly what the
// previous message form sent: the full name split into first and rest, email, optional phone,
// the topic as the single interest, and the message as notes.
const TOPICS = ['Membership', 'Personal Training', 'Reformer Pilates', 'MX4', 'Swim Lessons', 'Kids & Family', 'Massage', 'Rentals'];
const INITIAL = { name: '', email: '', phone: '', subject: '', message: '' };

const CONTACT_STEPS = [
  {
    label: 'Topic',
    question: () => 'What can we help with?',
    lede: 'Pick a topic or type your own, so it reaches the right person.',
    render: ({ form, set, fieldId, invalid, update }) => (
      <>
        <ul className="dv-journey__chips" aria-label="Common topics">
          {TOPICS.map((topic) => (
            <li key={topic}><button type="button" className="dv-journey__chip" aria-pressed={form.subject === topic} onClick={() => set('subject', topic)}>{topic}</button></li>
          ))}
        </ul>
        <div className="dv-journey__fields">
          <Field id={fieldId('subject')} label="Topic *" icon={MessageSquare} invalid={invalid('subject')}
            input={{ type: 'text', placeholder: 'Membership, swim lessons, personal training...', required: true, value: form.subject, onChange: update('subject') }} />
        </div>
      </>
    ),
  },
  {
    label: 'You',
    question: () => 'Who are we talking to?',
    lede: 'So the right team member knows who to reply to.',
    render: ({ form, fieldId, invalid, update }) => (
      <div className="dv-journey__fields">
        <Field id={fieldId('name')} label="Full Name *" icon={UserRound} invalid={invalid('name')}
          input={{ type: 'text', placeholder: 'Your full name', autoComplete: 'name', required: true, value: form.name, onChange: update('name') }} />
      </div>
    ),
  },
  {
    label: 'Reach',
    question: ({ firstName }) => `How should we reach you${firstName ? `, ${firstName}` : ''}?`,
    lede: 'We’ll only use this to reply to your message.',
    render: ({ form, fieldId, invalid, update }) => (
      <div className="dv-journey__fields">
        <Field id={fieldId('email')} label="Email *" icon={Mail} invalid={invalid('email')}
          input={{ type: 'email', placeholder: 'name@email.com', autoComplete: 'email', required: true, value: form.email, onChange: update('email') }} />
        <Field id={fieldId('phone')} label="Phone (optional)" icon={Phone} hint="Add a number if you’d like a call."
          input={{ type: 'tel', placeholder: '(925) 555-0123', autoComplete: 'tel', value: form.phone, onChange: update('phone') }} />
      </div>
    ),
  },
  {
    label: 'Message',
    question: () => 'What would you like to tell us?',
    lede: 'A sentence or two is plenty.',
    render: ({ form, fieldId, invalid, update }) => (
      <div className="dv-journey__fields">
        <Field id={fieldId('message')} label="Message *" invalid={invalid('message')} multiline
          input={{ required: true, rows: 5, value: form.message, onChange: update('message') }} />
      </div>
    ),
  },
  {
    label: 'Review',
    question: ({ firstName }) => `Ready to send${firstName ? `, ${firstName}` : ''}?`,
    lede: 'Take a look, then send it to our team.',
    render: () => <Privacy />,
  },
];

function toLead(form) {
  const nameParts = form.name.trim().split(/\s+/);
  const firstName = nameParts.shift() || '';
  return {
    first_name: firstName,
    last_name: nameParts.join(' '),
    email: form.email,
    phone: form.phone,
    location: 'Brentwood',
    interests: [form.subject],
    source: 'general-contact',
    notes: form.message,
  };
}

const ContactPage = () => {
  const contactSchema = {
    '@context': 'https://schema.org',
    '@type': 'HealthClub',
    name: 'Delta Valley Health Club',
    telephone: '(925) 240-2990',
    email: 'info@deltavac.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '120 Guthrie Ln',
      addressLocality: 'Brentwood',
      addressRegion: 'CA',
      postalCode: '94513',
    },
    openingHours: ['Mo-Th 04:30-22:30', 'Fr 04:30-21:00', 'Sa-Su 06:00-18:00'],
  };

  return (
    <div className="dv-inquiries dv-inquiries--contact" data-testid="contact-page">
      <SEO
        title="Contact Delta Valley Health Club | Brentwood & Discovery Bay"
        description="Contact Delta Valley Health Club in Brentwood or Discovery Bay. Call (925) 240-2990 or send our team a message."
        keywords="contact Delta Valley Health Club, Brentwood gym phone, Discovery Bay gym phone"
        schema={contactSchema}
      />

      <section className="dv-inquiries__hero">
        <div className="container-custom dv-inquiries__hero-inner">
          <p className="uppercase tracking-[0.25em] text-sm font-semibold text-blue-100 mb-4">
            Brentwood &amp; Discovery Bay
          </p>
          <h1 className="dv-size-heading text-5xl md:text-6xl font-bold mb-6">Contact Us</h1>
          <p className="dv-size-lede text-xl text-blue-100">
            Send us a message and the appropriate Delta Valley team member will follow up.
          </p>
        </div>
      </section>

      <InquiryJourney
        id="contact-message" testId="contact-journey" progressLabel="Message steps"
        welcome={{
          eyebrow: 'Leave a Message',
          title: ['Send us a message.', 'It takes about a minute.'],
          lede: 'Tell us what it’s about and how to reach you, and the right team member will follow up.',
          promises: [[Timer, 'About a minute'], [UserRound, 'Goes to the right team'], [Check, 'No commitment']],
          photo: 'C01',
        }}
        steps={CONTACT_STEPS}
        initial={INITIAL}
        payload={toLead}
        missing={(form) => (!form.subject.trim() ? 1 : !form.name.trim() ? 2 : !form.email ? 3 : !form.message.trim() ? 4 : -1)}
        plan={{
          title: 'Your message',
          rows: ({ form }) => [
            ['Topic', form.subject, 1],
            ['From', form.name.trim(), 2],
            ['Reach you at', [form.email, form.phone].filter(Boolean).join(' · '), 3],
            ['Message', form.message.trim(), 4],
          ],
        }}
        startLabel="Start My Message"
        submitLabel="Send Message"
        success={{
          title: 'Message Received',
          heading: ({ firstName }) => `Thanks${firstName ? `, ${firstName}` : ''}. We’ve got it.`,
          lede: 'The right Delta Valley team member will follow up.',
          next: [
            [MessageSquare, 'It reaches the right person', 'Your message goes to the team that handles your topic.'],
            [Mail, 'We reply', ({ form }) => `By email${form.phone ? ' or phone' : ''}.`],
            [MapPin, 'Or stop by', CLUB_ADDRESS],
          ],
          again: 'Send Another Message',
        }}
      />

      <section className="dv-inquiries__section">
        <div className="container-custom dv-inquiries__contact-layout dv-inquiries__tour-layout dv-inquiries__tour-layout--locations">
          <div className="dv-inquiries__locations space-y-5">
            <Card><CardContent className="p-6 space-y-5">
              <div className="flex gap-3"><MapPin className="text-[#0072BB] shrink-0" /><div><h2 className="dv-size-subheading font-bold">Brentwood Location</h2><p className="dv-size-body text-gray-600">120 Guthrie Ln., Brentwood, CA 94513</p></div></div>
              <div className="flex gap-3"><MapPin className="text-[#0072BB] shrink-0" /><div><h2 className="dv-size-subheading font-bold">Discovery Bay Location</h2><p className="dv-size-body text-gray-600">1520 Discovery Bay Blvd., Discovery Bay, CA 94505</p></div></div>
              <a href="tel:+19252402990" className="flex gap-3 text-[#0072BB] font-semibold"><Phone className="shrink-0" /> (925) 240-2990</a>
              <a href="mailto:info@deltavac.com" className="flex gap-3 text-[#0072BB] font-semibold"><Mail className="shrink-0" /> info@deltavac.com</a>
            </CardContent></Card>

            <Card><CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4"><Clock className="text-[#0072BB]" /><h2 className="dv-size-subheading text-xl font-bold">Brentwood Hours</h2></div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-3"><span>Monday–Thursday</span><strong>4:30am–10:30pm</strong></div>
                <div className="flex justify-between gap-3"><span>Friday</span><strong>4:30am–9:00pm</strong></div>
                <div className="flex justify-between gap-3"><span>Saturday–Sunday</span><strong>6:00am–6:00pm</strong></div>
              </div>
            </CardContent></Card>

            <Card><CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4"><Clock className="text-[#0072BB]" /><h2 className="dv-size-subheading text-xl font-bold">Discovery Bay Hours</h2></div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-3"><span>Monday–Thursday</span><strong>6:00am–8:00pm</strong></div>
                <div className="flex justify-between gap-3"><span>Friday</span><strong>6:00am–5:00pm</strong></div>
                <div className="flex justify-between gap-3"><span>Saturday–Sunday</span><strong>7:00am–2:00pm</strong></div>
              </div>
            </CardContent></Card>

            <Card><CardContent className="p-6">
              <h2 className="dv-size-subheading text-xl font-bold mb-4">Childcare Hours</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-3"><span>Monday–Thursday</span><strong>8:00am–8:00pm</strong></div>
                <div className="flex justify-between gap-3"><span>Friday–Sunday</span><strong>8:00am–1:00pm</strong></div>
              </div>
            </CardContent></Card>
          </div>
        </div>
      </section>

      <section className="dv-inquiries__section dv-inquiries__maps" aria-labelledby="visit-our-clubs-heading">
        <div className="container-custom">
          <h2 id="visit-our-clubs-heading" className="dv-size-heading mb-10 text-center text-3xl font-bold text-gray-900">Visit Our Clubs</h2>
          <GoogleMaps />
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
